import { WatchVideoBtn } from '@/components/WatchVideoBtn';
import cn from 'classnames';
import Image from 'next/image';
import { FC } from 'react';
import { ComingSoonOverlay } from './ComingSoonOverlay';

export type PostThumbProps = {
  category?: string;
  title?: string;
  theme?: 'light' | 'dark' | 'white';
  imageSrc?: string;
  href?: string;
  videoId?: string;
  onAssetClick?: (videoId: string) => void;
  isComingSoon?: boolean;
};

const getElementsTheme = (theme?: 'light' | 'dark' | 'white') => {
  const imageBackgroundClass = {
    light: 'bg-violet-300',
    dark: 'bg-violet-800',
    white: 'bg-white',
  }[theme || 'light'];

  const categoryClass = {
    light: 'text-gray-600',
    dark: 'text-citron-500',
    white: 'text-gray-600',
  }[theme || 'light'];

  const titleClass = {
    light: 'text-blue-900',
    dark: 'text-white',
    white: 'text-blue-900',
  }[theme || 'light'];

  return {
    imageBackgroundClass,
    categoryClass,
    titleClass,
  };
};

const PostThumb: FC<PostThumbProps> = ({
  category,
  title,
  theme,
  imageSrc = 'https://front.com/webassets/rebrand/promo-banner.webp',
  href = '#',
  videoId,
  onAssetClick,
  isComingSoon = false,
}) => {
  const { imageBackgroundClass, categoryClass, titleClass } = getElementsTheme(theme);

  return (
    <a
      href={isComingSoon || videoId ? undefined : href}
      aria-disabled={isComingSoon || undefined}
      className={cn(
        'flex flex-col gap-3',
        isComingSoon ? 'cursor-default select-none pointer-events-none' : 'group',
      )}
      onClick={() => onAssetClick?.(videoId ?? '')}
    >
      <div className={`${imageBackgroundClass} overflow-hidden relative rounded-[12px] aspect-video`}>
        {imageSrc && (
          <Image
            src={imageSrc}
            alt=""
            fill
            className={cn(
              'absolute w-full h-full object-cover transition-transform duration-1000',
              isComingSoon ? 'grayscale' : 'group-hover:scale-[1.1]',
            )}
          />
        )}

        {isComingSoon ? (
          <div className="absolute inset-0 flex items-center justify-center w-full h-full bg-white/40">
            <ComingSoonOverlay />
          </div>
        ) : null}

        {videoId && !isComingSoon ? <div className="absolute inset-0 flex items-center justify-center w-full h-full">
          <WatchVideoBtn />
        </div> : null}
      </div>

      <div className={cn('flex flex-col gap-[12px]', isComingSoon && 'opacity-60')}>
        {category && <span className={`${categoryClass} text-micro2 md:text-caption1 uppercase`}>{category}</span>}

        {title && (
          <div className={`${titleClass} block text-h4 font-500`}>
            <span className={cn(!isComingSoon && 'link-animated-underline')}>{title}</span>
          </div>
        )}
      </div>
    </a>
  );
};

export default PostThumb;
